import React, { useState, useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { formatPrice } from "../utils/formatPrice";
import { getOptimizedImageUrl, getImageSrcSet } from "../utils/imageUrl";
import "./SearchBar.css";

function SearchBar({
  onSearch,
  placeholder,
  autoFocus = false,
  alwaysVisible = false,
  suggestions = [],
  onSuggestionSelect,
  getTranslatedText,
}) {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [isExpanded, setIsExpanded] = useState(false);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  useEffect(() => {
    if (isExpanded && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isExpanded]);

  // Filter suggestions based on query
  const filteredSuggestions = useMemo(() => {
    if (!query.trim() || query.length < 2) return [];

    const lowerQuery = query.toLowerCase();
    return suggestions
      .filter((item) => {
        const name = getTranslatedText
          ? getTranslatedText(item, "name").toLowerCase()
          : (item.name || "").toLowerCase();
        const description = getTranslatedText
          ? getTranslatedText(item, "description").toLowerCase()
          : (item.description || "").toLowerCase();
        return name.includes(lowerQuery) || description.includes(lowerQuery);
      })
      .slice(0, 6); // Limit to 6 suggestions
  }, [query, suggestions, getTranslatedText]);

  // Close suggestions when clicking/touching outside
  useEffect(() => {
    const handleOutside = (e) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(e.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleOutside);
    document.addEventListener("touchstart", handleOutside, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handleOutside);
      document.removeEventListener("touchstart", handleOutside);
    };
  }, []);

  const handleChange = (e) => {
    const value = e.target.value;
    setQuery(value);
    setSelectedIndex(-1);
    setShowSuggestions(value.length >= 2);
    onSearch(value);
  };

  const handleClear = () => {
    setQuery("");
    setShowSuggestions(false);
    setSelectedIndex(-1);
    onSearch("");
    inputRef.current?.focus();
  };

  const handleToggle = () => {
    if (alwaysVisible) return;
    if (isExpanded && query) {
      handleClear();
    }
    setIsExpanded(!isExpanded);
    setShowSuggestions(false);
  };

  const handleBlur = () => {
    if (alwaysVisible) return;
    // Delay to allow click on suggestion
    setTimeout(() => {
      if (!query) {
        setIsExpanded(false);
      }
    }, 200);
  };

  const handleSuggestionClick = (item) => {
    const name = getTranslatedText
      ? getTranslatedText(item, "name")
      : item.name;
    setQuery(name);
    setShowSuggestions(false);
    onSearch(name);
    if (onSuggestionSelect) {
      onSuggestionSelect(item);
    }
  };

  const handleKeyDown = (e) => {
    if (!showSuggestions || filteredSuggestions.length === 0) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev < filteredSuggestions.length - 1 ? prev + 1 : 0,
        );
        break;
      case "ArrowUp":
        e.preventDefault();
        setSelectedIndex((prev) =>
          prev > 0 ? prev - 1 : filteredSuggestions.length - 1,
        );
        break;
      case "Enter":
        e.preventDefault();
        if (selectedIndex >= 0) {
          handleSuggestionClick(filteredSuggestions[selectedIndex]);
        }
        break;
      case "Escape":
        setShowSuggestions(false);
        setSelectedIndex(-1);
        break;
      default:
        break;
    }
  };

  return (
    <div
      className={`search-bar ${alwaysVisible ? "always-visible" : ""} ${isExpanded ? "expanded" : ""}`}
      ref={suggestionsRef}
    >
      {!alwaysVisible && (
        <button
          type="button"
          className="search-toggle"
          onClick={handleToggle}
          aria-label={t("search.toggle")}
        >
          <span className="material-icons" aria-hidden="true">
            {isExpanded && query ? "close" : "search"}
          </span>
        </button>
      )}

      <div className="search-input-wrapper">
        <input
          ref={inputRef}
          type="text"
          className="search-input"
          placeholder={placeholder || t("search.placeholder")}
          aria-label={t("search.ariaLabel")}
          value={query}
          onChange={handleChange}
          onBlur={handleBlur}
          onFocus={() => query.length >= 2 && setShowSuggestions(true)}
          onKeyDown={handleKeyDown}
          autoFocus={autoFocus}
          autoComplete="off"
        />
        {query && (
          <button
            className="search-clear"
            onClick={handleClear}
            aria-label={t("search.clear")}
          >
            <span className="material-icons" aria-hidden="true">close</span>
          </button>
        )}
      </div>

      {/* Autocomplete Suggestions */}
      {showSuggestions && filteredSuggestions.length > 0 && (
        <div className="search-suggestions">
          {filteredSuggestions.map((item, index) => {
            const name = getTranslatedText
              ? getTranslatedText(item, "name")
              : item.name;
            const category = item.categoryName || "";

            return (
              <button
                key={item.itemId}
                className={`search-suggestion ${index === selectedIndex ? "selected" : ""}`}
                onClick={() => handleSuggestionClick(item)}
                onMouseEnter={() => setSelectedIndex(index)}
              >
                {item.imageUrl ? (
                  <img
                    src={getOptimizedImageUrl(item.imageUrl, { width: 44 })}
                    srcSet={getImageSrcSet(item.imageUrl, 44)}
                    sizes="44px"
                    alt={name}
                    width={44}
                    height={44}
                    loading="lazy"
                    decoding="async"
                    className="suggestion-image"
                  />
                ) : (
                  <div className="suggestion-image-placeholder">
                    <span className="material-icons" aria-hidden="true">restaurant</span>
                  </div>
                )}
                <div className="suggestion-info">
                  <span className="suggestion-name">{name}</span>
                  {category && (
                    <span className="suggestion-category">{category}</span>
                  )}
                </div>
                <span className="suggestion-price">
                  ${formatPrice(item.price)}
                </span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export default SearchBar;
